import {
  listAccountScopedStorage,
  removeAccountScopedStorage,
  writeAccountScopedStorage,
} from "@/shared/storage/account-scope";
import {
  acknowledgeDeletionJournalMutations,
  advanceDeletionJournalAgainstRows,
  cancelDeletionJournalItem,
  DELETION_JOURNAL_DOMAINS,
  pendingDeletionJournal,
  queueDeletionJournalEntry,
  reconcileDeletionJournal,
  type DeletionJournalIo,
} from "@/shared/sync/deletion-journal-state.mjs";
import {
  nextAccountRowMutationIso,
  observeAccountRowMutationHighWater,
  ROW_MUTATION_DOMAINS,
} from "@/shared/sync/account-row-mutation-clock";

const DELETION_JOURNAL_PREFIX = "tb_public_web_sync_deletion_v2";

export type SyncTodoDeletion = {
  clientId: string;
  mutationId: string;
  deletedAt: string;
};

export type SyncSavedArticleDeletion = {
  articleId: string;
  mutationId: string;
  deletedAt: string;
};

export type SyncDeletionOutbox = {
  deletedTodos: SyncTodoDeletion[];
  deletedSavedArticles: SyncSavedArticleDeletion[];
};

function deletionJournalBaseKey(domain: string, slot: string): string {
  return `${DELETION_JOURNAL_PREFIX}:${domain}:${slot}`;
}

function journalEntries(domain: string): Array<{ slot: string; value: string }> {
  const baseKeyPrefix = `${DELETION_JOURNAL_PREFIX}:${domain}:`;
  return listAccountScopedStorage(baseKeyPrefix).map(({ baseKey, value }) => ({
    slot: baseKey.slice(baseKeyPrefix.length),
    value,
  }));
}

const deletionJournalIo: DeletionJournalIo = {
  list: journalEntries,
  write(domain, slot, value) {
    writeAccountScopedStorage(deletionJournalBaseKey(domain, slot), value);
  },
  remove(domain, slot) {
    removeAccountScopedStorage(deletionJournalBaseKey(domain, slot));
  },
};

export function pendingSyncDeletions(): SyncDeletionOutbox {
  return pendingDeletionJournal(deletionJournalIo);
}

export function queueTodoDeletion(
  clientId: string,
  observedUpdatedAt?: unknown,
): SyncTodoDeletion | null {
  return queueDeletionJournalEntry(
    deletionJournalIo,
    DELETION_JOURNAL_DOMAINS.todos,
    clientId,
    {
      deletedAt: nextAccountRowMutationIso(
        ROW_MUTATION_DOMAINS.todos,
        clientId,
        observedUpdatedAt,
      ),
    },
  ) as SyncTodoDeletion | null;
}

export function queueSavedArticleDeletion(
  articleId: string,
  observedUpdatedAt?: unknown,
): SyncSavedArticleDeletion | null {
  return queueDeletionJournalEntry(
    deletionJournalIo,
    DELETION_JOURNAL_DOMAINS.savedArticles,
    articleId,
    {
      deletedAt: nextAccountRowMutationIso(
        ROW_MUTATION_DOMAINS.savedArticles,
        articleId,
        observedUpdatedAt,
      ),
    },
  ) as SyncSavedArticleDeletion | null;
}

export function advancePendingSyncDeletionClocks(
  todos: Array<{ clientId?: unknown; updatedAt?: unknown }> | null | undefined,
  savedArticles:
    | Array<{ articleId?: unknown; updatedAt?: unknown }>
    | null
    | undefined,
): void {
  advanceDeletionJournalAgainstRows(deletionJournalIo, todos, savedArticles);
  const pending = pendingDeletionJournal(deletionJournalIo);
  for (const deletion of pending.deletedTodos) {
    observeAccountRowMutationHighWater(
      ROW_MUTATION_DOMAINS.todos,
      deletion.clientId,
      deletion.deletedAt,
    );
  }
  for (const deletion of pending.deletedSavedArticles) {
    observeAccountRowMutationHighWater(
      ROW_MUTATION_DOMAINS.savedArticles,
      deletion.articleId,
      deletion.deletedAt,
    );
  }
}

export function cancelTodoDeletion(clientId: string): void {
  cancelDeletionJournalItem(deletionJournalIo, DELETION_JOURNAL_DOMAINS.todos, clientId);
}

export function cancelSavedArticleDeletion(articleId: string): void {
  cancelDeletionJournalItem(
    deletionJournalIo,
    DELETION_JOURNAL_DOMAINS.savedArticles,
    articleId,
  );
}

export function acknowledgeSyncDeletionMutations(mutationIds: unknown): void {
  acknowledgeDeletionJournalMutations(deletionJournalIo, mutationIds);
}

export function reconcilePulledSyncDeletions(
  deletedTodos: Array<Partial<SyncTodoDeletion>> | null | undefined,
  deletedSavedArticles: Array<Partial<SyncSavedArticleDeletion>> | null | undefined,
): void {
  reconcileDeletionJournal(deletionJournalIo, deletedTodos, deletedSavedArticles);
}

export function hasPendingSyncDeletions(): boolean {
  const outbox = pendingSyncDeletions();
  return outbox.deletedTodos.length > 0 || outbox.deletedSavedArticles.length > 0;
}
